import {
  FieldTitle as OsFieldTitle,
  OsFieldTitleVersion,
  ResetToDefaultButton,
  useComponentVersion,
  type ResetToDefaultButtonProps,
} from 'opensolar-ui'
import { FunctionComponent, useMemo } from 'react'
import pure from 'recompose/pure'
import useTranslate from '../i18n/useTranslate'
import getFieldLabelTranslationArgs from './getFieldLabelTranslationArgs'

export type ResetToDefaultProps = {
  onReset: () => void
  value: string
}

const toResetToDefaultButtonProps = (
  resetToDefault: ResetToDefaultProps,
  translate: ReturnType<typeof useTranslate>
): ResetToDefaultButtonProps => ({
  onReset: resetToDefault.onReset,
  tooltip: translate('Click to reset to default (%{value})', { value: resetToDefault.value }),
  ariaLabel: translate('Reset to default'),
})

export const FieldTitle: FunctionComponent<{
  isRequired?: boolean
  resource?: string
  source?: string
  label?: string
  translate?: boolean
  labelAdornment?: string
  resetToDefault?: ResetToDefaultProps
}> = ({ resource, source, label, isRequired, labelAdornment, resetToDefault, ...rest }) => {
  const translate = useTranslate()
  const version = useComponentVersion('field_title')
  const autoTranslate = useMemo(
    () => translate(...getFieldLabelTranslationArgs({ label, resource, source })),
    [label, resource, source, translate]
  )
  const displayLabel = rest.translate === false ? label || '' : autoTranslate
  const resetToDefaultButton = useMemo(
    () => (resetToDefault ? toResetToDefaultButtonProps(resetToDefault, translate) : undefined),
    [resetToDefault, translate]
  )

  switch (version) {
    case OsFieldTitleVersion.V2:
      return (
        <OsFieldTitle
          labelAdornment={labelAdornment}
          label={displayLabel}
          resetToDefault={resetToDefaultButton}
          required={isRequired}
        />
      )
    default:
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%', minWidth: 0 }}>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayLabel}
            {isRequired && ' *'}
          </span>
          {resetToDefaultButton ? <ResetToDefaultButton {...resetToDefaultButton} /> : null}
        </span>
      )
  }
}

FieldTitle.displayName = 'FieldTitle'

export default pure(FieldTitle)
